import {
  BaseContactSchema,
  ContactAddressSchema,
  ContactEmailSchema,
  ContactFaxSchema,
  ContactNoteSchema,
  ContactPhoneSchema,
  ContactTagConnectionSchema,
  ContactWebsiteSchema,
} from "@/lib/data/schemas";
import {
  CitySchema,
  CountrySchema,
  EmailSchema,
  PhoneNumberSchema,
  StateSchema,
  StreetAddressSchema,
  ZipCodeSchema,
} from "@/lib/data/schemas/CommonSchemaFields";
import { z } from "zod";

/** API often returns `null` for empty strings; align draft form rows with EditContactForm coercion. */
const nullToEmptyString = (v: unknown): unknown => (v === null ? "" : v);

const draftFormString = () => z.preprocess(nullToEmptyString, z.string());

const contactFormAddressRowSchema = z.object({
  street1: z.union([z.literal(""), StreetAddressSchema]),
  street2: z.union([
    z.literal(""),
    z.string().max(200, { message: "Address line 2 is too long" }),
  ]),
  city: z.union([z.literal(""), CitySchema]),
  state: z.union([z.literal(""), StateSchema]),
  zipCode: z.union([z.literal(""), ZipCodeSchema]),
  country: z.union([z.literal(""), CountrySchema]),
});

/** Draft-friendly row shapes: empty strings are allowed; `superRefine` enforces when filled. */
export const contactFormPhoneItemSchema = ContactPhoneSchema.pick({
  id: true,
  extension: true,
  label: true,
  description: true,
  isPrimary: true,
  dateModified: true,
}).extend({
  number: draftFormString(),
});

export const contactFormEmailItemSchema = ContactEmailSchema.pick({
  id: true,
  description: true,
  label: true,
  isPrimary: true,
  dateModified: true,
}).extend({
  address: draftFormString(),
});

const contactFormFaxItemSchema = ContactFaxSchema.pick({
  id: true,
  label: true,
  description: true,
  isPrimary: true,
  extension: true,
  dateModified: true,
}).extend({
  number: draftFormString(),
});

const contactFormAddressItemSchema = ContactAddressSchema.pick({
  id: true,
  description: true,
  label: true,
  isPrimary: true,
  dateModified: true,
}).extend({
  county: z.string().nullable(),
  street1: z.string(),
  street2: z.string(),
  city: z.string(),
  state: z.string(),
  zipCode: z.string(),
  country: z.string(),
});

const contactFormWebsiteItemSchema = ContactWebsiteSchema.pick({
  id: true,
  description: true,
  label: true,
  dateModified: true,
}).extend({
  address: z.string(),
});

export const createContactFormSchema = BaseContactSchema.omit({
  firmId: true,
  dateCreated: true,
  dateModified: true,
  image: true,
  id: true,
})
  .partial()
  .required({ type: true })
  .extend({
    phones: z.array(contactFormPhoneItemSchema).optional(),
    emails: z.array(contactFormEmailItemSchema).optional(),
    addresses: z.array(contactFormAddressItemSchema).optional(),
    faxes: z.array(contactFormFaxItemSchema).optional(),
    websites: z.array(contactFormWebsiteItemSchema).optional(),
    notes: z
      .array(
        ContactNoteSchema.pick({
          id: true,
          message: true,
          draft: true,
          dateModified: true,
        }),
      )
      .optional(),
    tagConnections: z
      .array(
        ContactTagConnectionSchema.pick({
          id: true,
          tagId: true,
        }),
      )
      .optional(),
  })
  .superRefine((data, ctx) => {
    if (data.type === "individual") {
      if (!data.individualFirstName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["individualFirstName"],
          message: "First name is required",
          fatal: true,
        });
      }
      if (!data.individualLastName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["individualLastName"],
          message: "Last name is required",
          fatal: true,
        });
      }
    } else if (data.type === "organization") {
      if (!data.organizationName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["organizationName"],
          message: "Organization name is required",
          fatal: true,
        });
      }
    } else {
      if (!data.groupName) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ["groupName"],
          message: "Group name is required",
          fatal: true,
        });
      }
    }

    (data.emails ?? []).forEach((row, i) => {
      const v = row.address.trim();
      if (!v) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Email address is required",
          path: ["emails", i, "address"],
        });
        return;
      }
      const r = EmailSchema.safeParse(v);
      if (!r.success) {
        for (const issue of r.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["emails", i, "address"],
          });
        }
      }
    });

    (data.phones ?? []).forEach((row, i) => {
      const v = row.number.trim();
      if (!v) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Phone number is required",
          path: ["phones", i, "number"],
        });
        return;
      }
      const r = PhoneNumberSchema.safeParse(v);
      if (!r.success) {
        for (const issue of r.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["phones", i, "number"],
          });
        }
      }
    });

    (data.faxes ?? []).forEach((row, i) => {
      const v = row.number.trim();
      if (!v) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Fax number is required",
          path: ["faxes", i, "number"],
        });
        return;
      }
      const r = PhoneNumberSchema.safeParse(v);
      if (!r.success) {
        for (const issue of r.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["faxes", i, "number"],
          });
        }
      }
    });

    (data.addresses ?? []).forEach((row, i) => {
      const s1 = row.street1.trim();
      const c = row.city.trim();
      const st = row.state.trim();
      const zip = row.zipCode.trim();
      const co = row.country.trim();
      const s2 = row.street2.trim();
      const r = contactFormAddressRowSchema.safeParse({
        street1: s1,
        street2: s2,
        city: c,
        state: st,
        zipCode: zip,
        country: co,
      });
      if (!r.success) {
        for (const issue of r.error.issues) {
          const sub = issue.path[0] as
            | "street1"
            | "street2"
            | "city"
            | "state"
            | "zipCode"
            | "country"
            | undefined;
          if (sub) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: issue.message,
              path: ["addresses", i, sub],
            });
          }
        }
      }
    });

    (data.websites ?? []).forEach((row, i) => {
      const v = row.address.trim();
      if (!v) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "URL is required",
          path: ["websites", i, "address"],
        });
        return;
      }
      const r = z
        .string()
        .min(1, { message: "URL is required" })
        .max(250, { message: "URL is too long" })
        .safeParse(v);
      if (!r.success) {
        for (const issue of r.error.issues) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: issue.message,
            path: ["websites", i, "address"],
          });
        }
      }
    });

    (data.notes ?? []).forEach((row, i) => {
      const v = row.message?.trim() ?? "";
      if (!v) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Note is required",
          path: ["notes", i, "message"],
        });
      }
    });
  });

export type CreateContactFormValues = z.infer<typeof createContactFormSchema>;
export type ContactFormPhoneRow = z.infer<typeof contactFormPhoneItemSchema>;
export type ContactFormEmailRow = z.infer<typeof contactFormEmailItemSchema>;
